import { defineStore } from 'pinia'

import ProductType from '@/const/warehouse/ProductType'
import { generateUniqueId } from '@/utils'
import { OrderModel } from '@/models/order/order'
const orderModel = new OrderModel()
export const useOrderStore = defineStore('order', {
  state: () => ({
    orderList: [], // 购物车列表
    productMap: {}, // 商品id和商品信息的映射
    skuMap: {}, // sku id和sku信息的映射
    suitMap: {}, // 套装id和套装信息的映射
    packageMap: {}, // 套餐id和套餐信息的映射
    packageGroupMap: {}, // 套餐分组id和分组信息的映射
    packageProductMap: {}, // 套餐商品（固定和任选）id和商品信息的映射
    commissionStaffMap: {}, // 提成人id和员工信息的映射
    deskPackageProductList: {} // 台位id和台位商品列表的映射
  }),

  getters: {
    /**
     * 商品/套餐选中的数量
     */
    productNumMap: (state) => {
      const result = {}
      if (state.orderList) {
        state.orderList.forEach((item) => {
          const { product_id, package_id, num } = item
          const targetId = product_id || package_id
          if (result[targetId]) {
            result[targetId] += num
          } else {
            result[targetId] = num
          }
        })
      }
      return result
    }
  },

  actions: {
    // 设置商品map
    SET_PRODUCT_MAP(map) {
      this.productMap = map
    },

    // 设置商品套装map
    SET_SUIT_MAP(map) {
      this.suitMap = map
    },

    // 设置商品sku map
    SET_SKU_MAP(map) {
      this.skuMap = map
    },

    // 设置套餐map
    SET_PACKAGE_MAP(map) {
      this.packageMap = map
    },

    // 设置套餐分组map
    SET_PACKAGE_GROUP_MAP(map) {
      this.packageGroupMap = map
    },

    // 设置套餐商品map
    SET_PACKAGE_PRODUCT_MAP(map) {
      this.packageProductMap = map
    },

    // 保存提成人
    SET_COMMISSION_STAFF({ commissionStaffId, commissionStaff }) {
      this.commissionStaffMap[commissionStaffId] = commissionStaff
    },

    // 设置下单商品列表
    SET_ORDER_LIST(orderList) {
      this.orderList = orderList
    },

    // 清空下单商品列表
    CLEAR_ORDER_LIST() {
      this.orderList = []
    },

    // 添加下单商品
    ADD_ORDER_ITEM(orderItem) {
      this.orderList.push(orderItem)
    },

    // 删除下单商品
    REMOVE_ORDER_ITEM(orderItemId) {
      const removeIndex = this.orderList.findIndex((item) => item.id === orderItemId)
      if (removeIndex > -1) {
        this.orderList.splice(removeIndex, 1)
      }
    },

    // 修改下单商品数量
    CHANGE_ORDER_ITEM_NUM({ orderItemId, num }) {
      const targetOrderItem = this.orderList.find((item) => item.id === orderItemId)
      if (targetOrderItem) {
        targetOrderItem.num = num
      }
    },

    // 修改下单商品
    UPDATE_ORDER_ITEM({ orderItemId, orderItem }) {
      const index = this.orderList.findIndex((item) => item.id === orderItemId)

      if (index > -1) {
        this.orderList.splice(index, 1, {
          ...orderItem,
          id: orderItemId
        })
      }
    },

    RESET_STATE() {
      this.orderList = [] // 购物车列表
      this.productMap = {} // 商品id和商品信息的映射
      this.skuMap = {}
      this.suitMap = {}
      this.packageMap = {} // 套餐id和套餐信息的映射
      this.packageGroupMap = {}
      this.packageProductMap = {}
      this.commissionStaffMap = {} // 提成人id和员工信息的映射
    },

    /**
     * 初始化数据映射
     * @param list {Array} 全部商品套餐列表
     */
    initMap(list) {
      // 计算商品map 和 套餐map
      const productMap = {}
      const skuMap = {}
      const suitMap = {}
      const packageMap = {}
      const packageGroupMap = {}
      const packageProductMap = {}
      list.forEach((category) => {
        const { products, packages } = category

        if (category.type === ProductType.TYPE_PRODUCT) {
          products.forEach((productItem) => {
            // 保存商品信息
            productMap[productItem.id] = productItem
            // 保存套装信息
            if (productItem.product_suits) {
              productItem.product_suits.forEach((productSuit) => {
                suitMap[productSuit.id] = productSuit
              })
            }
            // 保存sku信息
            productItem.skus.forEach((sku) => {
              skuMap[sku.id] = sku
            })
          })
        } else if (category.type === ProductType.TYPE_PACKAGE) {
          packages.forEach((packageItem) => {
            // 保存套餐信息
            packageMap[packageItem.id] = packageItem

            packageItem.all_package_product_groups.forEach((group) => {
              const { fix_package_products, choose_any_package_products, ...extraInfo } = group
              // 保存分组信息
              packageGroupMap[group.id] = { ...extraInfo } // 不保存分组下的商品信息
              // 保存套餐商品信息
              fix_package_products.forEach((fixProduct) => {
                packageProductMap[fixProduct.id] = fixProduct
              })
              choose_any_package_products.forEach((chooseAnyProduct) => {
                packageProductMap[chooseAnyProduct.id] = chooseAnyProduct
              })
            })
          })
        }
      })

      // 更新数据
      this.SET_PRODUCT_MAP(productMap)
      this.SET_SKU_MAP(skuMap)
      this.SET_SUIT_MAP(suitMap)
      this.SET_PACKAGE_MAP(packageMap)
      this.SET_PACKAGE_GROUP_MAP(packageGroupMap)
      this.SET_PACKAGE_PRODUCT_MAP(packageProductMap)
    },

    /**
     * 添加下单商品
     */
    addOrderItem(e) {
      const orderItem = {
        id: generateUniqueId(), // 前端生成id
        type: e.type,
        num: e.number
      }

      // 添加套餐
      if (e.type === ProductType.TYPE_PACKAGE) {
        orderItem.package_id = e.package.id
        orderItem.all_package_product_groups = e.all_package_product_groups
        // 判断当前添加的套餐是否在购物车里，在则修改数量
        const index = orderModel.getPackageIndex(
          this.orderList,
          e.package.id,
          e.all_package_product_groups
        )

        if (index > -1) {
          this.CHANGE_ORDER_ITEM_NUM({
            orderItemId: this.orderList[index].id,
            num: this.orderList[index].num + orderItem.num
          })
          return
        }
        this.ADD_ORDER_ITEM(orderItem)
      } else if (e.type === ProductType.TYPE_PRODUCT) {
        orderItem.product_id = e.product.id
        orderItem.sku_id = e.sku.id
        orderItem.make_methods = e.make_methods // 做法
        if (e.suit) {
          orderItem.product_suit_id = e.suit.id
        }
        // 判断当前添加的商品是否在购物车里，在则修改数量
        const index = this.orderList.findIndex(({ product_id, product_suit_id, sku_id }) => {
          if (e.product.has_suit) {
            return (
              product_id === orderItem.product_id &&
              product_suit_id === orderItem.product_suit_id &&
              sku_id === orderItem.sku_id
            )
          }
          return product_id === orderItem.product_id && sku_id === orderItem.sku_id
        })

        if (index > -1) {
          this.CHANGE_ORDER_ITEM_NUM({
            orderItemId: this.orderList[index].id,
            num: this.orderList[index].num + orderItem.num
          })
          return
        }
        // 不一致，添加新的orderItem
        this.ADD_ORDER_ITEM(orderItem)
      }
    },

    /**
     * 加入购物车
     */
    addToCart(e) {
      const orderItem = {
        id: generateUniqueId(), // 前端生成id
        type: e.type,
        num: e.number
      }

      // console.log('加入购物车', e)
      if (e.commission_staffs) {
        orderItem.commission_staffs = e.commission_staffs.map((commissionStaff) => {
          return {
            id: commissionStaff.id,
            num: commissionStaff.num
          }
        })
        this.setCommissionStaffs(e.commission_staffs)
      }

      // 添加套餐
      if (e.type === ProductType.TYPE_PACKAGE) {
        orderItem.package_id = e.package.id
        orderItem.all_package_product_groups = e.all_package_product_groups
        // 判断当前添加的套餐是否在购物车里，在则添加数量
        // const index = orderModel.getPackageIndex(
        //   this.orderList,
        //   e.package.id,
        //   e.all_package_product_groups
        // )

        // if (index > -1) {
        //   this.CHANGE_ORDER_ITEM_NUM({
        //     orderItemId: this.orderList[index].id,
        //     num: this.orderList[index].num + orderItem.num // 加入购物车需要加上原购物车数量
        //   })
        //   this.addCommissionStaff({
        //     orderItemIndex: index,
        //     commission_staffs: e.commission_staffs
        //   })
        //   return
        // }
        this.ADD_ORDER_ITEM(orderItem)
      } else if (e.type === ProductType.TYPE_PRODUCT) {
        orderItem.product_id = e.product.id
        orderItem.sku_id = e.sku.id
        orderItem.make_methods = e.make_methods // 做法
        if (e.product.has_suit) {
          orderItem.product_suit_id = e.suit.id
        }
        // 判断当前添加的商品是否在购物车里，在则数量加一
        const index = this.orderList.findIndex(
          ({ product_id, product_suit_id, sku_id, make_methods }) => {
            const isMakeMethodsSame = orderModel.checkIsMakeMethodsSame(
              make_methods,
              orderItem.make_methods
            )
            if (e.product.has_suit) {
              return (
                product_id === orderItem.product_id &&
                product_suit_id === orderItem.product_suit_id &&
                sku_id === orderItem.sku_id &&
                isMakeMethodsSame
              )
            }
            return (
              product_id === orderItem.product_id &&
              sku_id === orderItem.sku_id &&
              isMakeMethodsSame
            )
          }
        )

        // if (index > -1) {
        //   this.CHANGE_ORDER_ITEM_NUM({
        //     orderItemId: this.orderList[index].id,
        //     num: this.orderList[index].num + orderItem.num // 加入购物车需要加上原购物车数量
        //   })
        //   this.addCommissionStaff({
        //     orderItemIndex: index,
        //     commission_staffs: e.commission_staffs
        //   })
        //   return
        // }
        // 不一致，添加新的orderItem
        this.ADD_ORDER_ITEM(orderItem)
      }
    },

    /**
     * 修改下单商品
     */
    updateOrderItem({ orderItemId, orderItem }) {
      if (orderItemId && this.orderList.some((item) => item.id === orderItemId)) {
        this.UPDATE_ORDER_ITEM({
          orderItemId,
          orderItem
        })
      }
    },

    /**
     * 减少下单商品
     */
    subOrderItem(e) {
      const targetOrderItem = this.orderList.find((orderItem) => {
        if (e.type === ProductType.TYPE_PRODUCT) {
          return (
            orderItem.product_id === e.product.id &&
            orderItem.sku_id === e.sku.id &&
            (!e.product.has_suit || (e.suit && orderItem.product_suit_id === e.suit.id))
          )
        }
        return orderItem.package_id === e.package.id
      })
      // console.log('减少下单商品', e, targetOrderItem)
      if (targetOrderItem) {
        if (e.number <= 0) {
          // 删除下单商品
          this.REMOVE_ORDER_ITEM(targetOrderItem.id)
        } else {
          // 修改下单商品数量
          this.CHANGE_ORDER_ITEM_NUM({
            orderItemId: targetOrderItem.id,
            num: e.number
          })
        }
      }
    },

    /**
     * 修改购物车数量
     */
    changeOrderItemNum(e) {
      if (e.number <= 0) {
        // 删除
        this.REMOVE_ORDER_ITEM(e.orderItemId)
      } else {
        this.CHANGE_ORDER_ITEM_NUM({
          orderItemId: e.orderItemId,
          num: e.number
        })
      }
    },

    /**
     * 修改购物车提成人数量
     */
    changeCommissionStaffNum(e) {
      const orderItemIndex = this.orderList.findIndex((orderItem) => orderItem.id === e.orderItemId)
      if (orderItemIndex === -1) {
        return
      }
      const commissionStaffIndex = this.orderList[orderItemIndex].commission_staffs.findIndex(
        (commissionStaff) => commissionStaff.id === e.commissionStaffId
      )
      if (commissionStaffIndex === -1) {
        return
      }
      if (e.number <= 0) {
        // 删除
        this.orderList[orderItemIndex].commission_staffs.splice(commissionStaffIndex, 1)
      } else {
        this.orderList[orderItemIndex].commission_staffs[commissionStaffIndex].num = e.number
      }
      this.changeOrderItemNum({
        orderItemId: e.orderItemId,
        number: getCommissionStaffTotalNum(this.orderList[orderItemIndex].commission_staffs)
      })
    },

    /**
     * 添加提成人
     */
    addCommissionStaff(e) {
      if (!e.commission_staffs) {
        return
      }
      const commission_staffs = this.orderList[e.orderItemIndex].commission_staffs
      e.commission_staffs.forEach((commissionStaff) => {
        const index = commission_staffs.findIndex(
          (oldCommissionStaff) => oldCommissionStaff.id === commissionStaff.id
        )
        // 提成人已存在
        if (index > -1) {
          this.orderList[e.orderItemIndex].commission_staffs[index].num += commissionStaff.num
        } else {
          this.orderList[e.orderItemIndex].commission_staffs.push(commissionStaff)
        }
      })
    },

    /**
     * 保存提成人信息
     */
    setCommissionStaffs(commissionStaffs) {
      commissionStaffs.forEach((commissionStaff) => {
        this.SET_COMMISSION_STAFF({
          commissionStaffId: commissionStaff.id,
          commissionStaff: commissionStaff.staff
        })
      })
    },

    /**
     * 修改提成人
     */
    updateCommissionStaffs(e) {
      const orderItemIndex = this.orderList.findIndex((orderItem) => orderItem.id === e.orderItemId)
      if (e.commission_staffs && orderItemIndex !== -1) {
        this.orderList[orderItemIndex].commission_staffs = e.commission_staffs
        // this.changeOrderItemNum({
        //   orderItemId: e.orderItemId,
        //   number: getCommissionStaffTotalNum(this.orderList[orderItemIndex].commission_staffs)
        // })
        this.setCommissionStaffs(e.commission_staffs)
      }
    },

    /**
     * 修改套餐商品做法
     */
    updatePackageProductMakeMethods(orderItemId, packageProduct, makeMethods) {
      const orderItemIndex = this.orderList.findIndex((orderItem) => orderItem.id === orderItemId)
      if (orderItemIndex !== -1 && packageProduct) {
        this.orderList[orderItemIndex].all_package_product_groups.forEach((group) => {
          ;[...group.fix_package_products, ...group.choose_any_package_products].forEach(
            (_packageProduct) => {
              if (_packageProduct.id === packageProduct.id) {
                _packageProduct.make_methods = makeMethods
              }
            }
          )
        })
      }
    },

    /**
     * 修改做法
     */
    updateOrderItemMakeMethods(orderItemId, makeMethods) {
      const orderItemIndex = this.orderList.findIndex((orderItem) => orderItem.id === orderItemId)
      if (orderItemIndex !== -1) {
        this.orderList[orderItemIndex].make_methods = makeMethods
      }
    },

    /**
     * 清空台位商品缓存
     */
    clearDeskPackageProductList() {
      this.deskPackageProductList = {}
    }
  }
})

/**
 * 获取提成人总数
 * @param commissionStaffs {Array} 提成人列表
 */
function getCommissionStaffTotalNum(commissionStaffs) {
  if (!commissionStaffs) {
    return 0
  }
  return commissionStaffs.reduce((sum, commissionStaff) => {
    return sum + commissionStaff.num
  }, 0)
}
